import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import axios from "axios"
import { Link } from "react-router-dom"
import { Spinner } from "reactstrap"
import { toast } from "react-toastify"
import { getCurrentUser } from "../../../helpers/Utils"
import { isMetaWaDesignDemo } from "../../../helpers/metaWhatsAppDesignDemo"

const authHeaders = () => ({
  "content-type": "application/json",
  Authorization: `Bearer ${
    getCurrentUser()?.token == null ? "" : getCurrentUser().token
  }`,
})

const companyBaseUrl = () =>
  `${process.env.REACT_APP_API_URL}/${localStorage.getItem("company_name")}`

const now = Date.now()
const minsAgo = m => new Date(now - m * 60 * 1000).toISOString()

/** Sample chats for design preview (used when inbox is empty) */
const DUMMY_CONVERSATIONS = [
  {
    id: "demo-1",
    contact_name: "Rahul Saini",
    wa_id: "917015550472",
    last_message_at: minsAgo(5),
    last_message: {
      body: "Sure, please share the invoice.",
      direction: "inbound",
      status: "received",
      status_at: minsAgo(5),
    },
    _demo: true,
  },
  {
    id: "demo-2",
    contact_name: "Priya Sharma",
    wa_id: "919876543210",
    last_message_at: minsAgo(42),
    last_message: {
      body: "Hello Priya, your order ORD-2041 has been confirmed.",
      direction: "outbound",
      status: "read",
      status_at: minsAgo(42),
    },
    _demo: true,
  },
  {
    id: "demo-3",
    contact_name: "Amit Verma",
    wa_id: "919811122233",
    last_message_at: minsAgo(120),
    last_message: {
      body: "Template: order_confirmation",
      direction: "outbound",
      status: "failed",
      status_at: minsAgo(120),
      error_message: "(#132000) Number of parameters does not match",
    },
    _demo: true,
  },
  {
    id: "demo-4",
    contact_name: "Neha Gupta",
    wa_id: "919700011122",
    last_message_at: minsAgo(180),
    last_message: {
      body: "Thank you!",
      direction: "inbound",
      status: "received",
      status_at: minsAgo(180),
    },
    _demo: true,
  },
]

const DUMMY_THREADS = {
  "demo-1": [
    {
      id: 1,
      direction: "outbound",
      body: "Hello Rahul, your information is saved with us.",
      status: "delivered",
      wa_timestamp: minsAgo(90),
      created_at: minsAgo(90),
    },
    {
      id: 2,
      direction: "inbound",
      body: "Hi Sofric Ai",
      status: "received",
      wa_timestamp: minsAgo(80),
      created_at: minsAgo(80),
    },
    {
      id: 3,
      direction: "outbound",
      body: "Hello Rahul, Your order ORD-1001 has been confirmed successfully.",
      status: "read",
      wa_timestamp: minsAgo(60),
      created_at: minsAgo(60),
    },
    {
      id: 4,
      direction: "inbound",
      body: "Sure, please share the invoice.",
      status: "received",
      wa_timestamp: minsAgo(5),
      created_at: minsAgo(5),
    },
  ],
  "demo-2": [
    {
      id: 11,
      direction: "outbound",
      body: "Hello Priya, your order ORD-2041 has been confirmed.",
      status: "sent",
      wa_timestamp: minsAgo(50),
      created_at: minsAgo(50),
    },
    {
      id: 12,
      direction: "outbound",
      body: "Hello Priya, your order ORD-2041 has been confirmed.",
      status: "delivered",
      wa_timestamp: minsAgo(48),
      created_at: minsAgo(48),
    },
    {
      id: 13,
      direction: "outbound",
      body: "Hello Priya, your order ORD-2041 has been confirmed.",
      status: "read",
      wa_timestamp: minsAgo(42),
      created_at: minsAgo(42),
    },
  ],
  "demo-3": [
    {
      id: 21,
      direction: "outbound",
      body: "Template: order_confirmation (en_US)",
      status: "failed",
      error_code: "132000",
      error_message: "(#132000) Number of parameters does not match",
      wa_timestamp: minsAgo(120),
      created_at: minsAgo(120),
    },
  ],
  "demo-4": [
    {
      id: 31,
      direction: "inbound",
      body: "I need product catalogue.",
      status: "received",
      wa_timestamp: minsAgo(200),
      created_at: minsAgo(200),
    },
    {
      id: 32,
      direction: "outbound",
      body: "Sure Neha, we will share it shortly.",
      status: "delivered",
      wa_timestamp: minsAgo(190),
      created_at: minsAgo(190),
    },
    {
      id: 33,
      direction: "inbound",
      body: "Thank you!",
      status: "received",
      wa_timestamp: minsAgo(180),
      created_at: minsAgo(180),
    },
  ],
}

const formatTime = value => {
  if (!value) return ""
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ""
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
}

const templateBodyText = (tpl) => {
  if (!tpl) return ""
  let components = tpl.components
  if (typeof components === "string") {
    try {
      components = JSON.parse(components)
    } catch (e) {
      components = []
    }
  }
  const body = (Array.isArray(components) ? components : []).find(
    c => String(c.type || "").toUpperCase() === "BODY"
  )
  return body?.text || tpl.body_preview || ""
}

const readableMessage = (msg, templates) => {
  const raw = String(msg?.body || "").trim()
  const payloadTemplate = msg?.payload?.request?.template
  const nameFromPayload = payloadTemplate?.name
  const nameMatch = raw.match(/^Template:\s*([A-Za-z0-9_]+)/)
  const prefixed = (templates || []).find(
    t => t?.name && (raw === t.name || raw.startsWith(`${t.name} `) || raw.startsWith(`${t.name}|`))
  )
  const name = nameFromPayload || (nameMatch && nameMatch[1]) || prefixed?.name || ""
  if (!name) return raw || msg?.type || "—"

  const tpl = prefixed || (templates || []).find(t => t.name === name)
  let text = templateBodyText(tpl)
  const components = payloadTemplate?.components || []
  const bodyComp = components.find(
    c => String(c.type || "").toLowerCase() === "body"
  )
  const params = (bodyComp?.parameters || []).map(p => p.text || "")
  if (!params.length && name && raw.startsWith(name + " ")) {
    params.push(
      ...raw
        .slice(name.length)
        .split("|")
        .map(part => part.trim())
        .filter(Boolean)
    )
  }
  if (text) {
    params.forEach((val, i) => {
      text = text.replace(`{{${i + 1}}}`, val || "")
    })
    return text
  }
  if (nameMatch) return raw.replace(/^Template:\s*/, "")
  return raw || msg?.type || "—"
}

const formatDay = value => {
  if (!value) return ""
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ""
  const today = new Date()
  if (d.toDateString() === today.toDateString()) return "Today"
  const yesterday = new Date()
  yesterday.setDate(today.getDate() - 1)
  if (d.toDateString() === yesterday.toDateString()) return "Yesterday"
  return d.toLocaleDateString()
}

const DeliveryTicks = ({ status, errorMessage }) => {
  const s = String(status || "").toLowerCase()
  if (s === "failed") {
    return (
      <span title={errorMessage || "Failed"} style={{ color: "#ea4335" }}>
        !
      </span>
    )
  }
  if (s === "read") {
    return <span title="Read" style={{ color: "#53bdeb" }}>✓✓</span>
  }
  if (s === "delivered") {
    return <span title="Delivered" style={{ color: "#8696a0" }}>✓✓</span>
  }
  if (s === "sent") {
    return <span title="Sent" style={{ color: "#8696a0" }}>✓</span>
  }
  return null
}

const detectBodyVarCount = tpl => {
  const body = (tpl?.components || []).find(
    c => String(c.type || "").toUpperCase() === "BODY"
  )
  const text = body?.text || tpl?.body_preview || ""
  const matches = String(text).match(/\{\{(\d+)\}\}/g) || []
  if (!matches.length) return 0
  return new Set(matches.map(m => m.replace(/[{}]/g, ""))).size
}

const detectHeaderMediaType = tpl => {
  const header = (tpl?.components || []).find(
    c => String(c.type || "").toUpperCase() === "HEADER"
  )
  const format = String(header?.format || "").toUpperCase()
  if (["IMAGE", "VIDEO", "DOCUMENT"].includes(format)) {
    return format.toLowerCase()
  }
  return null
}

const MetaWhatsAppInbox = () => {
  const [connected, setConnected] = useState(null)
  const [connection, setConnection] = useState(null)
  const [conversations, setConversations] = useState([])
  const [isDemo, setIsDemo] = useState(false)
  const [loadingList, setLoadingList] = useState(false)
  const [search, setSearch] = useState("")
  const [activeId, setActiveId] = useState(null)
  const [activeConversation, setActiveConversation] = useState(null)
  const [messages, setMessages] = useState([])
  const [loadingThread, setLoadingThread] = useState(false)
  const [templates, setTemplates] = useState([])
  const [templateKey, setTemplateKey] = useState("")
  const [bodyParams, setBodyParams] = useState([])
  const [headerLink, setHeaderLink] = useState("")
  const [sending, setSending] = useState(false)
  const [composerOpen, setComposerOpen] = useState(false)
  const threadEndRef = useRef(null)
  const activeIdRef = useRef(null)

  useEffect(() => {
    activeIdRef.current = activeId
  }, [activeId])

  const selectedTemplate = useMemo(() => {
    if (!templateKey) return null
    return (
      templates.find(t => `${t.name}||${t.language}` === templateKey) || null
    )
  }, [templateKey, templates])

  const headerMediaType = detectHeaderMediaType(selectedTemplate)
  const bodyVarCount = detectBodyVarCount(selectedTemplate)

  const filteredConversations = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return conversations
    return conversations.filter(
      c =>
        String(c.contact_name || "")
          .toLowerCase()
          .includes(q) || String(c.wa_id || "").includes(q)
    )
  }, [conversations, search])

  const fetchStatus = useCallback(() => {
    if (isMetaWaDesignDemo()) {
      setConnected(true)
      setConnection({ display_phone_number: "+91 70155 50472" })
      return Promise.resolve()
    }
    return axios
      .get(`${companyBaseUrl()}/whatsapp/meta/status`, {
        headers: authHeaders(),
      })
      .then(res => {
        if (res.data.status && res.data.data?.connected) {
          setConnected(true)
          setConnection(res.data.data.connection)
        } else {
          setConnected(false)
          setConnection(null)
        }
      })
      .catch(() => {
        setConnected(false)
        setConnection(null)
      })
  }, [])

  const fetchConversations = useCallback(() => {
    if (isMetaWaDesignDemo()) {
      setLoadingList(false)
      setConversations(DUMMY_CONVERSATIONS)
      setIsDemo(true)
      setActiveId("demo-1")
      setActiveConversation(DUMMY_CONVERSATIONS[0])
      setMessages(DUMMY_THREADS["demo-1"])
      return Promise.resolve()
    }
    setLoadingList(true)
    return axios
      .get(`${companyBaseUrl()}/whatsapp/meta/conversations`, {
        headers: authHeaders(),
        params: { per_page: 50 },
      })
      .then(res => {
        setLoadingList(false)
        if (res.data.status) {
          const rows = res.data.data?.data || []
          if (Array.isArray(rows) && rows.length > 0) {
            setConversations(rows)
            setIsDemo(false)
            if (
              !rows.some(r => String(r.id) === String(activeIdRef.current))
            ) {
              setActiveId(rows[0].id)
              setActiveConversation(rows[0])
            }
          } else {
            setConversations([])
            setIsDemo(false)
            setActiveId(null)
            setActiveConversation(null)
            setMessages([])
          }
        }
      })
      .catch(() => {
        setLoadingList(false)
        setConversations([])
        setIsDemo(false)
      })
  }, [])

  const fetchMessages = useCallback((conversationId, silent = false) => {
    if (!conversationId) return Promise.resolve()
    if (String(conversationId).startsWith("demo-")) {
      setActiveConversation(
        DUMMY_CONVERSATIONS.find(c => c.id === conversationId) || null
      )
      setMessages(DUMMY_THREADS[conversationId] || [])
      setLoadingThread(false)
      return Promise.resolve()
    }
    if (!silent) setLoadingThread(true)
    return axios
      .get(
        `${companyBaseUrl()}/whatsapp/meta/conversations/${conversationId}/messages`,
        {
          headers: authHeaders(),
          params: { order: "asc", per_page: 200 },
        }
      )
      .then(res => {
        if (!silent) setLoadingThread(false)
        if (res.data.status) {
          setActiveConversation(res.data.data?.conversation || null)
          const paginated = res.data.data?.messages
          const rows = paginated?.data || paginated || []
          setMessages(Array.isArray(rows) ? rows : [])
        }
      })
      .catch(() => {
        if (!silent) setLoadingThread(false)
      })
  }, [])

  const fetchTemplates = useCallback(() => {
    axios
      .get(`${companyBaseUrl()}/whatsapp/meta/templates`, {
        headers: authHeaders(),
        params: { status: "APPROVED" },
      })
      .then(res => {
        if (res.data.status) {
          const rows = res.data.data?.templates || []
          setTemplates(Array.isArray(rows) ? rows : [])
          if (rows.length > 0) {
            const key = `${rows[0].name}||${rows[0].language}`
            setTemplateKey(key)
            setBodyParams(Array(detectBodyVarCount(rows[0])).fill(""))
          }
        }
      })
      .catch(() => setTemplates([]))
  }, [])

  useEffect(() => {
    fetchStatus().then(() => {
      fetchConversations()
      fetchTemplates()
    })
  }, [fetchStatus, fetchConversations, fetchTemplates])

  useEffect(() => {
    if (!connected || isDemo) return undefined
    const timer = setInterval(() => {
      fetchConversations()
      if (
        activeIdRef.current &&
        !String(activeIdRef.current).startsWith("demo-")
      ) {
        fetchMessages(activeIdRef.current, true)
      }
    }, 12000)
    return () => clearInterval(timer)
  }, [connected, isDemo, fetchConversations, fetchMessages])

  useEffect(() => {
    threadEndRef.current?.scrollIntoView({ behavior: "auto" })
  }, [messages, activeId])

  const openConversation = conv => {
    setActiveId(conv.id)
    setComposerOpen(false)
    fetchMessages(conv.id)
  }

  const onSelectTemplate = key => {
    setTemplateKey(key)
    setHeaderLink("")
    const tpl = templates.find(t => `${t.name}||${t.language}` === key)
    setBodyParams(Array(detectBodyVarCount(tpl)).fill(""))
  }

  const sendTemplate = () => {
    if (isDemo || String(activeId).startsWith("demo-")) {
      toast.info("Sample chat — connect real conversations to send.", {
        position: "top-center",
        autoClose: 4000,
      })
      return
    }
    if (!activeConversation?.wa_id || !selectedTemplate) {
      toast.error("Select conversation and template.", {
        position: "top-center",
        autoClose: 4000,
      })
      return
    }
    if (headerMediaType && !headerLink.trim()) {
      toast.error(`Provide ${headerMediaType} header URL.`, {
        position: "top-center",
        autoClose: 4000,
      })
      return
    }
    if (bodyVarCount > 0 && bodyParams.some(v => !String(v || "").trim())) {
      toast.error("Fill all template variables.", {
        position: "top-center",
        autoClose: 4000,
      })
      return
    }

    const payload = {
      to: activeConversation.wa_id,
      type: "template",
      template_name: selectedTemplate.name,
      language_code: selectedTemplate.language || "en_US",
    }
    if (headerMediaType && headerLink.trim()) {
      payload.header_type = headerMediaType
      payload.header_link = headerLink.trim()
    }
    if (bodyVarCount > 0) {
      payload.body_params = bodyParams.map(v => String(v).trim())
    }

    setSending(true)
    axios
      .post(`${companyBaseUrl()}/whatsapp/meta/send-message`, payload, {
        headers: authHeaders(),
      })
      .then(res => {
        setSending(false)
        if (res.data.status) {
          toast.success(res.data.message || "Message sent.", {
            position: "top-center",
            autoClose: 4000,
          })
          setComposerOpen(false)
          fetchMessages(activeId)
          fetchConversations()
        } else {
          toast.error(res.data.message || "Send failed.", {
            position: "top-center",
            autoClose: 7000,
          })
        }
      })
      .catch(err => {
        setSending(false)
        toast.error(err?.response?.data?.message || "Send failed.", {
          position: "top-center",
          autoClose: 9000,
        })
      })
  }

  if (connected === false) {
    return (
      <div className="card border-0 shadow-sm">
        <div className="card-body text-center py-5">
          <h5>WhatsApp Business not connected</h5>
          <p className="text-muted mb-3">
            Connect your account to view and reply to conversations.
          </p>
          <Link to="/connect-whatsapp-meta" className="btn btn-success">
            Connection settings
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div
      className="meta-wa-inbox border bg-white"
      style={{
        height: 620,
        overflow: "hidden",
        borderRadius: 8,
        // Avoid global `.rounded:hover { transform: scale(1.1) }` zoom effect
        transform: "none",
        transition: "none",
      }}
    >
      {isDemo ? (
        <div
          className="px-3 py-2 small border-bottom"
          style={{ background: "#fff8e6", color: "#7a5b00" }}
        >
          Sample preview — design with dummy chats. Real chats replace this
          automatically when available.
        </div>
      ) : null}

      <div className="d-flex" style={{ height: isDemo ? "calc(100% - 38px)" : "100%" }}>
        {/* List */}
        <div
          className="border-end d-flex flex-column"
          style={{ width: 320, background: "#fff", flexShrink: 0 }}
        >
          <div className="p-3 border-bottom" style={{ background: "#f0f2f5" }}>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <strong>Chats</strong>
              <small className="text-muted">
                {connection?.display_phone_number || ""}
              </small>
            </div>
            <input
              type="search"
              className="form-control form-control-sm"
              placeholder="Search name or number…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <div className="flex-grow-1 overflow-auto">
            {loadingList && conversations.length === 0 ? (
              <div className="text-center py-4">
                <Spinner size="sm" color="success" />
              </div>
            ) : filteredConversations.length === 0 ? (
              <div className="text-muted small p-3 text-center">
                No conversations found.
              </div>
            ) : (
              filteredConversations.map(c => {
                const last = c.last_message
                const active = String(activeId) === String(c.id)
                return (
                  <button
                    type="button"
                    key={c.id}
                    className="w-100 text-start border-0 border-bottom px-3 py-2"
                    style={{
                      background: active ? "#e9edef" : "#fff",
                      cursor: "pointer",
                    }}
                    onClick={() => openConversation(c)}
                  >
                    <div className="d-flex justify-content-between gap-2">
                      <div className="fw-semibold text-truncate">
                        {c.contact_name || c.wa_id}
                      </div>
                      <small className="text-muted flex-shrink-0">
                        {formatTime(last?.status_at || c.last_message_at)}
                      </small>
                    </div>
                    <div className="d-flex align-items-center gap-1 small text-muted">
                      {last?.direction === "outbound" ? (
                        <DeliveryTicks
                          status={last.status}
                          errorMessage={last.error_message}
                        />
                      ) : null}
                      <span className="text-truncate">
                        {readableMessage(last, templates)}
                      </span>
                    </div>
                    {String(last?.status).toLowerCase() === "failed" &&
                    last?.error_message ? (
                      <div
                        className="small text-truncate mt-1"
                        style={{ color: "#ea4335" }}
                      >
                        {last.error_message}
                      </div>
                    ) : null}
                  </button>
                )
              })
            )}
          </div>
        </div>

        {/* Thread */}
        <div
          className="flex-grow-1 d-flex flex-column"
          style={{ background: "#efeae2", minWidth: 0 }}
        >
          {!activeId ? (
            <div className="flex-grow-1 d-flex align-items-center justify-content-center text-muted">
              Select a chat to view messages
            </div>
          ) : (
            <>
              <div
                className="px-3 py-2 border-bottom d-flex justify-content-between align-items-center"
                style={{ background: "#f0f2f5" }}
              >
                <div>
                  <div className="fw-semibold">
                    {activeConversation?.contact_name ||
                      activeConversation?.wa_id ||
                      "Chat"}
                  </div>
                  <small className="text-muted">
                    {activeConversation?.wa_id}
                  </small>
                </div>
                {!isDemo ? (
                  <button
                    type="button"
                    className="btn btn-sm btn-light"
                    onClick={() => fetchMessages(activeId)}
                  >
                    Refresh
                  </button>
                ) : null}
              </div>

              <div className="flex-grow-1 overflow-auto px-3 py-3">
                {loadingThread ? (
                  <div className="text-center py-4">
                    <Spinner size="sm" color="success" />
                  </div>
                ) : messages.length === 0 ? (
                  <div className="text-center text-muted small py-4">
                    No messages in this conversation.
                  </div>
                ) : (
                  messages.map((m, idx) => {
                    const outbound = m.direction === "outbound"
                    const prev = messages[idx - 1]
                    const day = formatDay(m.wa_timestamp || m.created_at)
                    const prevDay = formatDay(
                      prev?.wa_timestamp || prev?.created_at
                    )
                    const showDay = day && day !== prevDay
                    return (
                      <React.Fragment key={m.id}>
                        {showDay ? (
                          <div className="text-center my-2">
                            <span
                              className="badge"
                              style={{
                                background: "#ffffff",
                                color: "#54656f",
                                fontWeight: 500,
                              }}
                            >
                              {day}
                            </span>
                          </div>
                        ) : null}
                        <div
                          className={`d-flex mb-2 ${
                            outbound
                              ? "justify-content-end"
                              : "justify-content-start"
                          }`}
                        >
                          <div
                            style={{
                              maxWidth: "75%",
                              background: outbound ? "#d9fdd3" : "#fff",
                              borderRadius: 8,
                              padding: "6px 10px 4px",
                              boxShadow: "0 1px 0.5px rgba(11,20,26,.13)",
                            }}
                          >
                            <div
                              style={{
                                whiteSpace: "pre-wrap",
                                wordBreak: "break-word",
                                fontSize: 14,
                                color: "#111b21",
                              }}
                            >
                              {readableMessage(m, templates)}
                            </div>
                            {String(m.status).toLowerCase() === "failed" &&
                            (m.error_message || m.error_code) ? (
                              <div
                                className="small mt-1"
                                style={{ color: "#ea4335" }}
                              >
                                {m.error_message ||
                                  `(#${m.error_code}) Failed`}
                              </div>
                            ) : null}
                            <div
                              className="d-flex justify-content-end align-items-center gap-1 mt-1"
                              style={{ fontSize: 11, color: "#667781" }}
                            >
                              <span>
                                {formatTime(m.wa_timestamp || m.created_at)}
                              </span>
                              {outbound ? (
                                <DeliveryTicks
                                  status={m.status}
                                  errorMessage={m.error_message}
                                />
                              ) : null}
                            </div>
                          </div>
                        </div>
                      </React.Fragment>
                    )
                  })
                )}
                <div ref={threadEndRef} />
              </div>

              <div
                className="border-top p-3"
                style={{ background: "#f0f2f5" }}
              >
                {!composerOpen ? (
                  <button
                    type="button"
                    className="btn btn-success w-100"
                    onClick={() => setComposerOpen(true)}
                  >
                    Send template message
                  </button>
                ) : (
                  <div
                    className="bg-white p-3 border"
                    style={{ borderRadius: 8, transform: "none" }}
                  >
                    <div className="d-flex justify-content-between mb-2">
                      <strong className="small">New template message</strong>
                      <button
                        type="button"
                        className="btn btn-sm btn-link text-muted p-0"
                        onClick={() => setComposerOpen(false)}
                      >
                        Cancel
                      </button>
                    </div>
                    {isDemo ? (
                      <div className="small text-muted">
                        Sample chat preview — sending is disabled until real
                        conversations load.
                      </div>
                    ) : templates.length === 0 ? (
                      <div className="small text-muted">
                        No approved templates.{" "}
                        <Link to="/whatsapp-meta-templates">
                          Manage templates
                        </Link>
                      </div>
                    ) : (
                      <>
                        <label className="form-label small mb-1">Template</label>
                        <select
                          className="form-select form-select-sm mb-2"
                          value={templateKey}
                          onChange={e => onSelectTemplate(e.target.value)}
                        >
                          {templates.map(t => (
                            <option
                              key={`${t.id}-${t.name}-${t.language}`}
                              value={`${t.name}||${t.language}`}
                            >
                              {t.name} ({t.language})
                            </option>
                          ))}
                        </select>
                        {headerMediaType ? (
                          <input
                            className="form-control form-control-sm mb-2"
                            placeholder={`${headerMediaType} URL *`}
                            value={headerLink}
                            onChange={e => setHeaderLink(e.target.value)}
                          />
                        ) : null}
                        {bodyVarCount > 0
                          ? bodyParams.map((val, i) => (
                              <input
                                key={i}
                                className="form-control form-control-sm mb-2"
                                placeholder={`{{${i + 1}}} *`}
                                value={val}
                                onChange={e => {
                                  const next = [...bodyParams]
                                  next[i] = e.target.value
                                  setBodyParams(next)
                                }}
                              />
                            ))
                          : null}
                        <button
                          type="button"
                          className="btn btn-success btn-sm w-100"
                          disabled={sending}
                          onClick={sendTemplate}
                        >
                          {sending ? "Sending…" : "Send"}
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default MetaWhatsAppInbox
